import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createEmployee, updateEmployeeDetails, monthlyCTC, getSingleEmployeeDetails,
  getEmployeePublishedPayslips, getEmployeeTimeline,
  getCTCrevision, deleteCTCrevision,
  createExcelEmployeeData,
  createExcelBankEmployeeData,
  updatepassword,
  updatePhoto,
  resignedEmp,
  reactivateEmployee,
  uploadDocument,
  updateWorkdays,
  updateLoans,
  getSalaryPreview,
  updateEmployeeLeaves,
  GET_LITE_EMPLOYEE_DETAILS,
  GET_LITE_EMPLOYEE_DETAILS_ALL,
  uploadBulkEmployeeMaster,
  getEmployeeAssets,
  addEmployeeAsset,
  deleteEmployeeAsset
} from '../api'
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import SLUGS from '../resources/slugs';




export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();

  return useMutation({
    mutationFn: createEmployee,

    onSuccess: () => {
      showSuccess('Employee created successfully!');

      queryClient.invalidateQueries(['employeeList']);
    },

    onError: async (error) => {
      console.error('Error creating employee:', error);

      let errorMessage = 'Error while creating employee';

      try {
        // Handle Axios-style error (React Query uses fetch or axios under the hood)
        const data = error.response?.data || (await error.json?.()) || error;

        if (typeof data === 'object' && data !== null) {
          // Backend wraps field errors as { error: "...", details: { field: [...] } }
          // (see EmployeeDetailView.put) rather than a flat { field: [...] } shape —
          // unwrap `details` first so nested field errors render as text instead of
          // stringifying the object into "[object Object]".
          const fieldErrors = (data.details && typeof data.details === 'object') ? data.details : data;
          errorMessage = Object.entries(fieldErrors)
            .map(([field, messages]) =>
              `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages} `
            )
            .join('\n');
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } catch (e) {
        console.warn('Could not parse error response:', e);
      }

      showError(errorMessage, { position: 'top-right', autoHideDuration: 6000 });
    }

  });
};

// Hook to fetch single employee details
export const useGetSingleEmployeeDetails = (id) => {
  return useQuery({
    queryKey: ['singleEmployeeDetails', id],
    queryFn: getSingleEmployeeDetails,  // Returns nested data: {employee: {...}, ctc_items: [...], ...}
    enabled: !!id,
    refetchOnWindowFocus: false, // Prevents overwriting local state when switching windows
  });
};

export const useEmployeeTimeline = (employeeNo) => {
  return useQuery({
    queryKey: ['employeeTimeline', employeeNo],
    queryFn: getEmployeeTimeline,
    enabled: !!employeeNo,
    refetchOnWindowFocus: false,
  });
};

// Returns the same EmployeeSerializer payload but with `payslips` overridden to the
// structured Payslip rows (month/year/status, last 6 months, server-filtered to PUBLISHED).
export const useGetEmployeePublishedPayslips = (id) => {
  return useQuery({
    queryKey: ['employeePublishedPayslips', id],
    queryFn: getEmployeePublishedPayslips,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();
  const navigate = useNavigate();

  return useMutation({
    // Pass a function that calls `updateEmployeeDetails` with the required arguments
    mutationFn: ({ employeeNo, formData }) => updateEmployeeDetails({ employeeNo, formData }),
    onSuccess: (data, variables) => {
      // console.log('Employee updated successfully:', data);
      showSuccess("Employee updated successfully")

      // Invalidate relevant queries to update cache
      queryClient.invalidateQueries(['employeeDetails', variables.employeeNo]); // Refresh specific employee details
      queryClient.invalidateQueries(['employeeList']); // Refresh the employee list
      navigate(SLUGS.employees)

    },
    onError: async (error) => {
      console.error('Error creating employee:', error);

      let errorMessage = 'Error while creating employee';

      try {
        // Handle Axios-style error (React Query uses fetch or axios under the hood)
        const data = error.response?.data || (await error.json?.()) || error;

        if (typeof data === 'object' && data !== null) {
          // Backend wraps field errors as { error: "...", details: { field: [...] } }
          // (see EmployeeDetailView.put) rather than a flat { field: [...] } shape —
          // unwrap `details` first so nested field errors render as text instead of
          // stringifying the object into "[object Object]".
          const fieldErrors = (data.details && typeof data.details === 'object') ? data.details : data;
          errorMessage = Object.entries(fieldErrors)
            .map(([field, messages]) =>
              `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages} `
            )
            .join('\n');
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } catch (e) {
        console.warn('Could not parse error response:', e);
      }

      showError(errorMessage, { position: 'top-right', autoHideDuration: 6000 });
    }
  });
};

export const useMonthlyCTC = () => {
  const { showError, showSuccess } = useAlert();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employee_no, monthly_ctc, amount, from_date, remarks, pf }) => monthlyCTC({ employee_no, monthly_ctc, amount, from_date, remarks, pf }),
    onSuccess: (data, variables) => {
      // console.log(data);
      showSuccess("Employee Salary Updated")
      queryClient.invalidateQueries(['employeeList']);
    },
    onError: (error) => {
      showError(`Error:${error?.response?.data?.error} `)
      console.error("Error Update Monthly CTC", error)
    }
  })
};

export const useUpdateWorkdays = () => {
  const { showError, showSuccess } = useAlert();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateWorkdays(data),
    onSuccess: (data, variables) => {
      showSuccess("Workdays updated and salary recalculated successfully");
      queryClient.invalidateQueries({ queryKey: ['singleEmployee', variables.employee_no] });
    },
    onError: (error) => {
      showError(`Error: ${error?.response?.data?.error} `);
      console.error("Error update workdays", error);
    }
  });
};

export const useUpdateDeduction = () => {
  const { showError } = useAlert();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateLoans(data),
    onSuccess: (data, variables) => {
      // v5 object form — the bare-array form silently no-ops in React Query v5, so the
      // page wouldn't refetch after an inline earning/deduction edit.
      queryClient.invalidateQueries({ queryKey: ['singleEmployee', variables.employee_no] });
    },
    onError: (error) => {
      showError(`Error: ${error?.response?.data?.error} `);
      console.error("Error update deductions", error);
    }
  });
};

export const useAllEmployeeDetails = () => {
  return useQuery({
    queryKey: ['employeeList'],
    queryFn: GET_LITE_EMPLOYEE_DETAILS, // Using lite version for faster loading
    staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    placeholderData: (prev) => prev, // Essential: avoids resetting to empty while refetching
    // Respect staleTime: serve cached data instantly on revisit (refetch only
    // when stale) instead of hitting the API on every mount.
    refetchOnMount: true,
    retry: 2,
    onError: (error) => {
      console.error('Error fetching employee details:', error);
    },
  });
};

// Full directory (active + resigned) with the `resigned` flag, for the Contacts
// page. Separate query key so it never disturbs the active-only ['employeeList'].
export const useAllEmployeeDetailsWithResigned = () => {
  return useQuery({
    queryKey: ['employeeList', 'withResigned'],
    queryFn: GET_LITE_EMPLOYEE_DETAILS_ALL,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    placeholderData: (prev) => prev,
    refetchOnMount: true,
    retry: 2,
  });
};

// Key sits under ['singleEmployee', id] so the existing edit invalidations refresh it too.
export const useSalaryPreview = ({ employeeNo, month, year }) => {
  return useQuery({
    queryKey: ['singleEmployee', employeeNo, 'salaryPreview', month, String(year)],
    queryFn: () => getSalaryPreview({ employeeNo, month, year }),
    enabled: !!employeeNo && !!month && !!year,
    retry: false,
  });
};

export const useGetSingleEmployee = ({ id }) => {
  // console.log(id);
  return useQuery({
    queryKey: ['singleEmployee', id], // Pass an array as the key
    queryFn: getSingleEmployeeDetails,   // Use the nested data API function
    enabled: !!id,                   // Only fetch if id is truthy
  });
};

export const useUpdateEmployeeLeaves = () => {
  const { showError, showSuccess } = useAlert();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeNo, leaveData }) => updateEmployeeLeaves({ employeeNo, leaveData }),
    onSuccess: (data, variables) => {
      showSuccess("Employee leaves updated successfully");
      // Patch the cached Leave Tracker report in place so the row updates
      // INSTANTLY, without waiting on a refetch. `data` is the API response
      // (total_leaves / sick_leaves / casual_leaves of the saved employee).
      queryClient.setQueryData(['leaveSummaryReport'], (old) =>
        Array.isArray(old)
          ? old.map((emp) =>
              String(emp.employee_no) === String(variables.employeeNo)
                ? {
                    ...emp,
                    total_leaves: data?.total_leaves ?? emp.total_leaves,
                    sick_leaves: data?.sick_leaves ?? emp.sick_leaves,
                    casual_leaves: data?.casual_leaves ?? emp.casual_leaves,
                  }
                : emp
            )
          : old
      );
      // v5 requires the { queryKey } object form — the bare-array form silently
      // matches nothing. Also revalidate in the background.
      queryClient.invalidateQueries({ queryKey: ['singleEmployee', variables.employeeNo] });
      queryClient.invalidateQueries({ queryKey: ['leaveSummaryReport'] });
    },
    onError: (error) => {
      showError(`Error: ${error?.response?.data?.error || error.message} `);
      console.error("Error updating leaves", error);
    }
  });
};

export const useEmployeeCTCrevisions = ({ id }) => {
  return useQuery({

    queryKey: ['ctcrevision', id], // Pass an array as the key
    queryFn: getCTCrevision,      // Use an API function compatible with v5
    enabled: !!id,                   // Only fetch if `id` is truthy
  });
};
export const useDeleteCTCrevisions = () => {
  const { showError, showSuccess } = useAlert();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteCTCrevision(id),
    onSuccess: () => {
      // Invalidate and refetch relevant queries, e.g., an employee list
      showSuccess("Deleted Succesfully")
      queryClient.invalidateQueries(['ctcrevision']); // Replace with your query key
    },
    onError: (error) => {
      showError(`Error:${error?.response?.data?.error} `)
      console.error('Error deleting employee:', error);
    },
  });
};

export const useExcelUploadEmployeeData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeDataArray) => createExcelEmployeeData(employeeDataArray),
    onSuccess: () => {
      // Invalidate and refetch relevant queries, e.g., an employee list
      queryClient.invalidateQueries(['employeeList']); // Replace with your query key
      alert("Employee Added");
      queryClient.refetchQueries(['employeeList']);
    },
    onError: (error) => {
      console.error('Error uploading employee data:', error);
      alert("Error uploading employee data");
    },
  });
};
export const useExcelBankUploadEmployeeData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeDataArray) => createExcelBankEmployeeData(employeeDataArray),
    onSuccess: () => {
      // Invalidate and refetch relevant queries, e.g., an employee list
      queryClient.invalidateQueries(['employeeList']);
      queryClient.refetchQueries(['employeeList']); // Replace with your query key
      alert("Bank details Added")
    },
    onError: (error) => {
      console.error('Error uploading employee data:', error);
      alert("Error while adding Bankdetails")
    },
  });
};

export const useUploadBulkEmployeeMaster = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();

  return useMutation({
    mutationFn: (file) => uploadBulkEmployeeMaster(file),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['employeeList']);
      const res = data.results;
      showSuccess(`Upload Complete: ${res.successful} success, ${res.failed} failed.`);
    },
    onError: (error) => {
      const msg = error.response?.data?.error || "Upload failed";
      showError(msg);
    },
  });
};

export const useUpdatePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatepassword,
    onSuccess: (data) => {
      // console.log('Employee created successfully:', data);
      alert("Updated Succesfully");
      queryClient.invalidateQueries(['singleEmployee']);



    },
    onError: (error) => {
      console.error('Error creating employee:', error);
      alert("Error While Creating");

    },
  });
};


export const useUpdatePhoto = () => {
  const queryClient = useQueryClient();


  return useMutation({
    mutationFn: updatePhoto,
    onSuccess: (data) => {
      // console.log('Employee created successfully:', data);
      alert("Updated Succesfully");
      queryClient.invalidateQueries(['singleEmployee']);
      queryClient.invalidateQueries(['singleEmployeeDetails']);
      queryClient.invalidateQueries(['employeeList']);
      queryClient.invalidateQueries(['employeeListDetailedForBirthdays']);
    },
    onError: (error) => {
      console.error('Error creating employee:', error);
      alert("Error While Creating");

    },
  });
};

export const useResignedEmpDetails = () => {
  return useQuery({
    queryKey: ['resignedEmp'],
    queryFn: resignedEmp,
    // The resigned list rarely changes — serve it from cache for 5 min so
    // re-opening the page is instant instead of re-fetching (and showing a
    // spinner) every visit. Reactivating an employee invalidates this key.
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
};

export const useReactivateEmployee = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();

  return useMutation({
    mutationFn: reactivateEmployee,
    onSuccess: (data) => {
      showSuccess(data?.message || 'Employee reactivated successfully');
      queryClient.invalidateQueries(['resignedEmp']);
      queryClient.invalidateQueries(['employeeList']);
    },
    onError: (error) => {
      showError(error?.response?.data?.error || 'Error reactivating employee');
      console.error('Error reactivating employee:', error);
    },
  });
};

export const useUploadDocument = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();

  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: (data, variables) => {
      showSuccess("Document Uploaded Successfully");
      queryClient.invalidateQueries(['singleEmployeeDetails', variables.employee_no]);
    },
    onError: (error) => {
      console.error('Error uploading document:', error);
      showError("Error uploading document. Please try again.");
    },
  });
};

export const useGetEmployeeAssets = (employeeNo) => {
  return useQuery({
    queryKey: ['employeeAssets', employeeNo],
    queryFn: () => getEmployeeAssets(employeeNo),
    enabled: !!employeeNo,
  });
};

export const useAddEmployeeAsset = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();

  return useMutation({
    mutationFn: ({ employeeNo, assetData, file }) => addEmployeeAsset({ employeeNo, assetData, file }),
    onSuccess: (data, variables) => {
      showSuccess("Asset assigned successfully");
      queryClient.invalidateQueries(['employeeAssets', variables.employeeNo]);
    },
    onError: (error) => {
      showError("Error assigning asset");
      console.error(error);
    }
  });
};

export const useDeleteEmployeeAsset = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlert();

  return useMutation({
    mutationFn: (assetId) => deleteEmployeeAsset(assetId),
    onSuccess: (data, variables) => {
      showSuccess("Asset removed successfully");
      queryClient.invalidateQueries(['employeeAssets']);
      queryClient.invalidateQueries(['singleEmployeeDetails']);
    },
    onError: (error) => {
      showError("Error removing asset");
      console.error(error);
    }
  });
};
